import { Injectable } from '@angular/core';
import { GiftCardService } from './giftcard.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Product } from '../../order/models/product';
import { ConnectionService } from '../../../services/connection/connection.service';
import { GiftCard } from '../models/giftcard';

import { LoggerService } from '../../../services/logger/logger.service';


@Injectable()
export class GiftCardOdooService implements GiftCardService {
  constructor(
    private connectionService: ConnectionService,
    private loggerService: LoggerService,
  ) {
  }

  getGiftCardAvailableAmount(orderNumber: number, giftCardNumber: string, orderAmount: number): Observable<number> {
    return this._getGiftCardAvailableAmount('/store/gift-card/available-amount', orderNumber, giftCardNumber, orderAmount);
  }

  getGuestGiftCardAvailableAmount(orderNumber: number, giftCardNumber: string, orderAmount: number): Observable<number> {
    return this._getGiftCardAvailableAmount('/store/gift-card/guest-available-amount', orderNumber, giftCardNumber, orderAmount);
  }

  private _getGiftCardAvailableAmount(url: string, orderNumber: number, giftCardNumber: string, orderAmount: number): Observable<number> {
    if (!giftCardNumber || !orderAmount || orderAmount <= 0) {
      return of(0);
    }

    return this.connectionService.sendJsonPostRequest(url, {
      order_id: orderNumber,
      cardId: giftCardNumber,
      orderAmount
    }).pipe(map((result) => {
      if (!result || !result.amount) {
        return 0;
      }

      return result.amount;
    }));
  }

  applyOrderGiftCardPayments(orderNumber: number, giftCardRedeems: GiftCard[]): Observable<GiftCard[]> {
    if (!orderNumber || !giftCardRedeems || !giftCardRedeems.length) {
      return of([]);
    }

    return this.connectionService.sendJsonPostRequest('/store/payment/apply_order_gift_card_payments', {
      order_id: orderNumber,
      card_ids: giftCardRedeems.map(x => x.cardNumber),
    }).pipe(map(
      result => {
        if (!result || result.length <= 0) {
          return null;
        }

        return result.map(x => GiftCard.createFromAny(x));
      }));
  }


  refundGiftCards(orderNumber: number, giftCards: GiftCard[]): Observable<GiftCard[]> {
    if (!orderNumber || !giftCards || !giftCards.length) {
      return of([]);
    }

    return this.connectionService.sendJsonPostRequest('/store/payment/refund_gift_cards', {
      order_id: orderNumber,
      cards: giftCards,
    }).pipe(map(
      result => {
        if (!result || result.length <= 0) {
          return [];
        }

        if (result.error_message) {
          this.loggerService.error(result.error_message);
          return [];
        }

        return result.map(x => GiftCard.createFromAny(x));
      }));
  }

  recalculateGiftCardAmount(orderNumber: number, giftCardNumbers: string[], orderAmount: number): Observable<GiftCard[]> {
    return this._recalculateGiftCardAmount(
      '/store/gift-card/recalculate_gift_cards_amount',
      orderNumber,
      giftCardNumbers,
      orderAmount
    );
  }

  guestRecalculateGiftCardAmount(orderNumber: number, giftCardNumbers: string[], orderAmount: number): Observable<GiftCard[]> {
    return this._recalculateGiftCardAmount(
      '/store/gift-card/guest-recalculate-gift-cards-amount',
      orderNumber,
      giftCardNumbers,
      orderAmount
    );
  }

  private _recalculateGiftCardAmount(
    url: string,
    orderNumber: number,
    giftCardNumbers: string[],
    orderAmount: number
  ): Observable<GiftCard[]> {
    if (!giftCardNumbers || !giftCardNumbers.length) {
      return of([]);
    }

    return this.connectionService.sendJsonPostRequest(url, {
      cards: giftCardNumbers,
      amount: orderAmount,
      order_id: orderNumber,
    }).pipe(map(
      result => {
        if (!result || result.length <= 0) {
          return [];
        }

        if (result.error_message) {
          this.loggerService.error(result.error_message);
          return [];
        }

        return result.map(x => GiftCard.createFromAny(x));
      }));
  }
}
